import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../api";
import Layout from "../components/Layout";
import { CHAMPS_TRONC } from "./champsInspection";
import ModaleConfirmation from "../components/ModaleConfirmation";

// Coquille commune des wizards d'inspection (VGP, CT). La navigation, la
// sauvegarde progressive, la reprise, les defauts, les photos, la finalisation
// et l'abandon sont geres ici. Chaque type fournit :
//   - titre           : libelle affiche dans l'en-tete
//   - blocsDetails    : [{ cle, champs }] -> tables de details (vgp_details / ct_details)
//   - etapes          : [{ num, titre }]
//   - valider(num, form) -> messages bloquants
//   - renderEtape(num, ctx) -> contenu de l'etape

function formDepuis(insp, blocsDetails) {
  const form = {};
  CHAMPS_TRONC.forEach((k) => (form[k] = insp[k] ?? ""));
  form.validite_mois = insp.validite_mois ?? 6;
  form.details = {};
  blocsDetails.forEach(({ cle, champs }) => {
    const src = insp[cle] || {};
    champs.forEach((k) => (form.details[k] = src[k] ?? null));
  });
  return form;
}

function construirePayload(form, etapeCible, blocsDetails) {
  const payload = { etape_courante: etapeCible };
  CHAMPS_TRONC.forEach((k) => {
    const v = form[k];
    if (v !== "" && v !== null && v !== undefined) payload[k] = v;
  });
  blocsDetails.forEach(({ cle, champs }) => {
    const det = {};
    champs.forEach((k) => {
      const v = form.details?.[k];
      if (v !== "" && v !== null && v !== undefined) det[k] = v;
    });
    if (Object.keys(det).length > 0) payload[cle] = det;
  });
  return payload;
}

// LE MOT PAR DEFAUT EST « DEFAUT » (H1, 28/09/2026). La VGP passe
// « observation » ; le controle technique ne passe rien et garde son vocabulaire
// -- decision de Yanis, la VGP SEULEMENT. Le mot vit en parametre plutot qu'en
// condition sur le type d'inspection : ajouter un troisieme parcours demain ne
// demandera pas de rouvrir ce fichier.
export const VOCABULAIRE_DEFAUT = {
  ajoutImpossible: "Ajout du défaut impossible.",
  enregistrementImpossible: "Enregistrement du défaut impossible.",
  titreSuppression: "Supprimer ce défaut ?",
  pluriel: "défauts",
};

export default function InspectionWizard({
  user, onLogout, titre, blocsDetails, etapes, valider, renderEtape,
  vocabulaire = VOCABULAIRE_DEFAUT,
}) {
  const NB = etapes.length;
  const { vehiculeId, inspectionId } = useParams();
  const navigate = useNavigate();

  const [inspection, setInspection] = useState(null);
  const [vehicule, setVehicule] = useState(null);
  const [form, setForm] = useState(null);
  const [defauts, setDefauts] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [apercus, setApercus] = useState({});
  const [etape, setEtape] = useState(1);
  // Étape la plus avancée atteinte dans la session courante.
  // Détermine quelles étapes sont directement cliquables dans la barre.
  const [etapeMax, setEtapeMax] = useState(1);
  const [erreursEtape, setErreursEtape] = useState([]);
  const [erreurGlobale, setErreurGlobale] = useState("");
  const [sauvegarde, setSauvegarde] = useState("");
  const [chargement, setChargement] = useState(true);
  const [confirmation, setConfirmation] = useState(null);
  // Le PDF met une cinquantaine de millisecondes a se fabriquer, mais le
  // reseau, lui, prend le temps qu'il prend : sans cet etat, le bouton ne
  // disait rien et l'utilisateur cliquait a nouveau.
  const [pdfEnCours, setPdfEnCours] = useState(false);
  // Y a-t-il quelque chose a sauvegarder ? Sert a l'avertissement de fermeture
  // ET a l'indicateur : « Enregistre » affiche alors qu'on vient de taper est
  // un mensonge, et c'est celui qui a fait croire a un audit que des ecritures
  // se perdaient en silence.
  const [modifie, setModifie] = useState(false);

  // Reflète toujours l'état courant pour les callbacks (cleanup, autosave).
  const etatRef = useRef({});
  etatRef.current = { form, etape, inspection, modifie };

  // Mis à true avant toute sortie gérée (retourFiche, finaliser, abandonner)
  // pour éviter qu'un PUT supplémentaire soit envoyé par le cleanup d'unmount.
  const quitteManagedRef = useRef(false);

  useEffect(() => {
    api
      .get(`/inspections/${inspectionId}`)
      .then(({ data }) => {
        setInspection(data);
        setForm(formDepuis(data, blocsDetails));
        setDefauts(data.defauts || []);
        setPhotos(data.photos || []);
        const etapeDepart = Math.min(Math.max(data.etape_courante || 1, 1), NB);
        setEtape(etapeDepart);
        setEtapeMax(etapeDepart); // étapes 1..etapeDepart sont navigables dès la reprise
      })
      // Un 404 n'est pas une panne (audit B3, 28/09/2026) : la ressource n'existe
      // pas -- ou appartient a un autre client, que le serveur ne confirme
      // jamais. « Impossible de charger » envoyait chercher un probleme de
      // reseau. Meme message que la fiche vehicule.
      .catch((err) =>
        setErreurGlobale(err?.response?.status === 404
          ? "Inspection introuvable."
          : "Impossible de charger l'inspection."))
      .finally(() => setChargement(false));
    api.get(`/vehicules/${vehiculeId}`).then(({ data }) => setVehicule(data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inspectionId, vehiculeId]);

  // ── Sauvegarde en cas de navigation non gérée (ex. bouton Retour du navigateur,
  //    clic sur un lien React Router depuis un autre composant). Fire-and-forget
  //    silencieux : on ne met pas à jour l'UI car le composant est en train de
  //    se démonter.
  useEffect(() => {
    return () => {
      if (quitteManagedRef.current) return; // déjà sauvegardé par retourFiche/finaliser/abandonner
      const { form: f, inspection: insp, etape: e } = etatRef.current;
      if (!f || insp?.statut !== "en_cours") return;
      api
        .put(`/inspections/${inspectionId}`, construirePayload(f, e, blocsDetails))
        .catch(() => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Protection contre la fermeture d'onglet ou navigation externe.
  //    On ne peut pas garantir un PUT asynchrone dans ce cas (best-effort).
  //    L'alerte navigateur prévient l'utilisateur que des données pourraient
  //    ne pas être sauvegardées.
  useEffect(() => {
    const handler = (e) => {
      const { inspection: insp, modifie: sale } = etatRef.current;
      // L'avertissement ne se declenche QUE s'il reste quelque chose a perdre.
      // Il partait auparavant des que l'inspection etait en cours, meme apres
      // une sauvegarde reussie : le navigateur criait « modifications non
      // enregistrees » pendant que l'ecran affichait « Enregistre ». Deux
      // messages contradictoires, dont l'un devait etre faux -- c'etait
      // celui-ci.
      if (insp?.statut !== "en_cours" || !sale) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, []);

  // ── Autosave périodique (toutes les 20 secondes).
  useEffect(() => {
    const timer = setInterval(() => {
      const { inspection: insp, etape: e } = etatRef.current;
      if (insp?.statut === "en_cours") envoyerPut(e);
    }, 20000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    photos.forEach((p) => {
      if (apercus[p.id]) return;
      api
        .get(`/photos/${p.id}/fichier`, { responseType: "blob" })
        .then((resp) => {
          const url = URL.createObjectURL(resp.data);
          setApercus((m) => (m[p.id] ? m : { ...m, [p.id]: url }));
        })
        .catch(() => {});
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photos]);

  // Adresses temporaires des apercus PDF ouverts : liberees au depart de
  // l'ecran. Sans cela, chaque apercu laisse son document en memoire du
  // navigateur pour toute la duree de l'onglet.
  const urlsPdfRef = useRef([]);
  useEffect(() => () => urlsPdfRef.current.forEach(URL.revokeObjectURL), []);

  const apercusRef = useRef({});
  apercusRef.current = apercus;
  useEffect(() => () => Object.values(apercusRef.current).forEach(URL.revokeObjectURL), []);

  const modifiable = inspection?.statut === "en_cours";

  // ── Sauvegarde principale (met à jour l'indicateur d'état dans l'UI).
  async function envoyerPut(etapeCible) {
    const { form: f, inspection: insp } = etatRef.current;
    if (!f || insp?.statut !== "en_cours") return;
    setSauvegarde("en cours");
    try {
      await api.put(`/inspections/${inspectionId}`, construirePayload(f, etapeCible, blocsDetails));
      setSauvegarde("ok");
      setModifie(false);
    } catch {
      // On NE remet PAS `modifie` a faux : la saisie n'est pas partie, et
      // l'avertissement de fermeture doit continuer de proteger l'utilisateur.
      setSauvegarde("erreur");
    }
  }

  // Toute frappe rend le formulaire « modifie ». Sans cela, l'en-tete gardait
  // « Enregistre » de la sauvegarde PRECEDENTE pendant qu'on saisissait : le
  // mot etait vrai une minute plus tot, et faux au moment ou on le lisait.
  const majTronc = (champ, valeur) => {
    setModifie(true);
    setForm((f) => ({ ...f, [champ]: valeur }));
  };
  const majDetails = (champ, valeur) => {
    setModifie(true);
    setForm((f) => ({ ...f, details: { ...f.details, [champ]: valeur } }));
  };

  const ajouterDefaut = async () => {
    try {
      const { data } = await api.post(`/inspections/${inspectionId}/defauts`, { description: "" });
      setDefauts((liste) => [...liste, data]);
    } catch {
      setErreurGlobale(vocabulaire.ajoutImpossible);
    }
  };
  const majDefautLocal = (defautId, description) =>
    setDefauts((liste) => liste.map((d) => (d.id === defautId ? { ...d, description } : d)));
  const sauverDefaut = async (defaut) => {
    if (!modifiable) return;
    try {
      await api.put(`/defauts/${defaut.id}`, { description: defaut.description ?? "" });
    } catch {
      setErreurGlobale(vocabulaire.enregistrementImpossible);
    }
  };
  const supprimerDefaut = (defaut) => {
    setConfirmation({
      titre: vocabulaire.titreSuppression,
      message: defaut.description?.trim() || "(aucune description)",
      libelleConfirmer: "Supprimer",
      danger: true,
      onConfirmer: async () => {
        await api.delete(`/defauts/${defaut.id}`);
        setDefauts((liste) => liste.filter((d) => d.id !== defaut.id));
      },
    });
  };

  const onUploadPhotos = async (e) => {
    const fichiers = Array.from(e.target.files || []);
    e.target.value = "";
    if (fichiers.length === 0) return;
    const donnees = new FormData();
    fichiers.forEach((f) => donnees.append("files", f));
    try {
      const { data } = await api.post(`/inspections/${inspectionId}/photos`, donnees);
      setPhotos((liste) => [...liste, ...data]);
    } catch (err) {
      const st = err?.response?.status;
      setErreurGlobale(
        st === 415
          ? "Format non autorisé (JPEG, PNG ou WebP)."
          : st === 413
          ? "Fichier trop volumineux (10 Mo max)."
          : "Envoi des photos impossible."
      );
    }
  };
  const majLegendeLocale = (photoId, legende) =>
    setPhotos((liste) => liste.map((p) => (p.id === photoId ? { ...p, legende } : p)));
  const sauverLegende = async (photo) => {
    if (!modifiable) return;
    try {
      await api.put(`/photos/${photo.id}`, { legende: photo.legende ?? "" });
    } catch {
      setErreurGlobale("Enregistrement de la légende impossible.");
    }
  };
  const supprimerPhoto = (photo) => {
    setConfirmation({
      titre: "Supprimer cette photo ?",
      message: "Elle ne figurera plus dans le rapport.",
      libelleConfirmer: "Supprimer",
      danger: true,
      onConfirmer: async () => {
        await api.delete(`/photos/${photo.id}`);
        setPhotos((liste) => liste.filter((p) => p.id !== photo.id));
        setApercus((m) => {
          const copie = { ...m };
          if (copie[photo.id]) URL.revokeObjectURL(copie[photo.id]);
          delete copie[photo.id];
          return copie;
        });
      },
    });
  };

  // « APERCU » DOIT MONTRER, PAS TELECHARGER.
  //
  // Le bouton s'appelait « Apercu PDF » sur un brouillon et deposait un
  // fichier dans le dossier de telechargements : rien n'apparaissait a
  // l'ecran. Sur une demonstration, le livrable central du produit avait
  // l'air casse.
  //
  // L'ONGLET S'OUVRE AVANT L'APPEL RESEAU, et c'est la seule facon de faire.
  // Un `window.open` appele APRES un `await` n'est plus rattache au clic de
  // l'utilisateur : le navigateur le prend pour une fenetre surgissante et le
  // bloque. On ouvre donc l'onglet dans la foulee du clic, puis on y pose
  // l'adresse du document une fois recu.
  //
  // Le document TERMINE, lui, se telecharge : c'est une piece a archiver.
  const ouvrirPdf = async () => {
    if (pdfEnCours) return;
    setErreurGlobale("");
    setPdfEnCours(true);
    const apercu = modifiable;
    const onglet = apercu ? window.open("", "_blank") : null;
    try {
      const resp = await api.get(`/inspections/${inspectionId}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      if (apercu && onglet) {
        onglet.location = url;
        // L'adresse temporaire ne se revoque PAS tout de suite : l'onglet ne
        // l'a pas encore lue, et il afficherait une page vide. Elle est
        // liberee au depart de l'ecran (voir le nettoyage plus bas).
        urlsPdfRef.current.push(url);
      } else {
        // Onglet refuse par le navigateur, ou document terminé : on retombe
        // sur l'enregistrement du fichier plutot que de ne rien faire.
        const lien = document.createElement("a");
        lien.href = url;
        lien.download = `inspection_${inspectionId}.pdf`;
        document.body.appendChild(lien);
        lien.click();
        lien.remove();
        URL.revokeObjectURL(url);
      }
    } catch {
      if (onglet) onglet.close();
      setErreurGlobale(
        apercu ? "Aperçu du PDF impossible." : "Téléchargement du PDF impossible."
      );
    } finally {
      setPdfEnCours(false);
    }
  };

  // ── Sortie gérée vers la fiche véhicule : sauvegarde puis navigation.
  const retourFiche = async () => {
    quitteManagedRef.current = true;
    if (modifiable) await envoyerPut(etape);
    navigate(`/vehicules/${vehiculeId}`);
  };

  // ── Navigation séquentielle.
  const allerSuivant = async () => {
    const errs = valider(etape, form);
    if (errs.length > 0) {
      setErreursEtape(errs);
      return;
    }
    setErreursEtape([]);
    const cible = Math.min(etape + 1, NB);
    if (modifiable) await envoyerPut(cible);
    setEtape(cible);
    setEtapeMax((prev) => Math.max(prev, cible)); // étend la plage navigable
  };
  const allerPrecedent = async () => {
    const cible = Math.max(etape - 1, 1);
    setErreursEtape([]);
    if (modifiable) await envoyerPut(cible);
    setEtape(cible);
  };

  // ── Navigation directe vers une étape déjà atteinte.
  //    Aucune validation requise (l'utilisateur a déjà validé cette étape).
  const allerEtape = async (numCible) => {
    if (numCible === etape || numCible > etapeMax || sauvegarde === "en cours") return;
    setErreursEtape([]);
    if (modifiable) await envoyerPut(numCible);
    setEtape(numCible);
  };

  const finaliser = async () => {
    const errs = valider(etape, form);
    if (errs.length > 0) {
      setErreursEtape(errs);
      return;
    }
    setErreurGlobale("");
    if (modifiable) await envoyerPut(etape);
    try {
      await api.post(`/inspections/${inspectionId}/finaliser`);
      quitteManagedRef.current = true; // inspection terminée, cleanup inutile
      navigate(`/vehicules/${vehiculeId}`);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setErreurGlobale(typeof d === "string" ? d : "Finalisation impossible.");
    }
  };
  const abandonner = () => {
    setConfirmation({
      titre: "Abandonner ce brouillon ?",
      sousTitre: vehicule?.immatriculation,
      message:
        "Tout ce qui a été saisi dans cette inspection est perdu, y compris les "
        + `${vocabulaire.pluriel} et les photos. Cette action est irréversible.`,
      libelleConfirmer: "Abandonner",
      danger: true,
      onConfirmer: async () => {
        await api.delete(`/inspections/${inspectionId}`);
        quitteManagedRef.current = true; // inspection supprimée, cleanup inutile
        navigate(`/vehicules/${vehiculeId}`);
      },
    });
  };

  if (chargement) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <p>Chargement…</p>
      </Layout>
    );
  }
  if (!inspection) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="erreur">{erreurGlobale || "Inspection introuvable."}</div>
        <button className="btn" onClick={() => navigate(`/vehicules/${vehiculeId}`)}>
          ← Fiche véhicule
        </button>
      </Layout>
    );
  }

  const ro = !modifiable;
  const ctx = {
    form, ro, vehicule, majTronc, majDetails,
    defauts, ajouterDefaut, majDefautLocal, sauverDefaut, supprimerDefaut,
    photos, apercus, onUploadPhotos, majLegendeLocale, sauverLegende, supprimerPhoto,
    modifiable,
  };
  const titreEtape = etapes.find((e) => e.num === etape)?.titre;

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="entete-page">
        <button className="btn" onClick={retourFiche}>
          ← Fiche véhicule
        </button>
        <div className="wizard-actions-entete">
          {sauvegarde === "en cours" && <span className="sauvegarde-etat">Enregistrement…</span>}
          {sauvegarde !== "en cours" && modifie && (
            <span className="sauvegarde-etat">Modifications non enregistrées</span>
          )}
          {sauvegarde === "ok" && !modifie && (
            <span className="sauvegarde-etat">Enregistré</span>
          )}
          {sauvegarde === "erreur" && <span className="erreur-champ">Échec de l'enregistrement</span>}
          <button className="btn" onClick={ouvrirPdf} disabled={pdfEnCours}>
            {pdfEnCours
              ? "…"
              : modifiable
              ? "Aperçu PDF"
              : "Télécharger le PDF"}
          </button>
          {modifiable && (
            <button className="btn" onClick={abandonner}>
              Abandonner
            </button>
          )}
        </div>
      </div>

      <div className="entete-fiche">
        <h2>
          {titre}
          {vehicule ? ` — ${vehicule.immatriculation}` : ""}
        </h2>
        <span className={inspection.statut === "terminee" ? "badge-cat" : "badge"}>
          {inspection.statut === "terminee" ? "Terminée" : "En cours"}
        </span>
      </div>

      {!modifiable && (
        <p className="note-info">
          Inspection terminée : consultation seule (aucune modification possible).
        </p>
      )}
      {erreurGlobale && <div className="erreur">{erreurGlobale}</div>}

      {confirmation && (
        <ModaleConfirmation
          {...confirmation}
          onFermer={() => setConfirmation(null)}
        />
      )}

      {/* Indicateur compact affiché sur mobile à la place de la barre complète */}
      <div className="wizard-etapes-compact">
        <span className="wizard-compact-texte">
          Étape {etape} / {NB} — {titreEtape}
        </span>
        <div className="wizard-compact-barre">
          <div
            className="wizard-compact-progression"
            style={{ width: `${Math.round((etape / NB) * 100)}%` }}
          />
        </div>
      </div>

      <ol className="wizard-etapes">
        {etapes.map((e) => {
          const atteinte = e.num <= etapeMax;
          const estActif = e.num === etape;
          const cliquable = atteinte && !estActif && sauvegarde !== "en cours";
          return (
            <li
              key={e.num}
              className={
                "wizard-etape" +
                (estActif ? " actif" : "") +
                (atteinte && !estActif ? " fait" : "") +
                (cliquable ? " wizard-etape-cliquable" : "")
              }
              onClick={cliquable ? () => allerEtape(e.num) : undefined}
              role={cliquable ? "button" : undefined}
              title={
                !atteinte
                  ? "Validez les étapes précédentes pour accéder à celle-ci"
                  : undefined
              }
            >
              <span className="wizard-num">{e.num}</span>
              <span className="wizard-titre">{e.titre}</span>
            </li>
          );
        })}
      </ol>

      <div className="wizard-contenu">
        <h3>
          Étape {etape} — {titreEtape}
        </h3>
        {erreursEtape.length > 0 && (
          <ul className="wizard-erreurs">
            {erreursEtape.map((msg, i) => (
              <li key={i} className="erreur-champ">
                {msg}
              </li>
            ))}
          </ul>
        )}
        {renderEtape(etape, ctx)}
      </div>

      <div className="wizard-nav">
        <button
          className="btn"
          onClick={allerPrecedent}
          disabled={etape === 1 || sauvegarde === "en cours"}
        >
          Précédent
        </button>
        {etape < NB ? (
          <button className="btn-accent" onClick={allerSuivant} disabled={sauvegarde === "en cours"}>
            Suivant
          </button>
        ) : (
          modifiable && (
            <button className="btn-accent" onClick={finaliser}>
              Finaliser l'inspection
            </button>
          )
        )}
      </div>
    </Layout>
  );
}
